import { useEffect, useState } from 'react'
import { Link, NavLink, Navigate, useLocation } from 'react-router-dom'
import { Menu, X, ChevronDown, UtensilsCrossed, ShoppingBag, CalendarCheck, Mail, MessageCircle, MapPin } from 'lucide-react'
import { useTenant } from '../../contexts/TenantContext'
import { t, LanguageSwitcher } from '../../i18n'
import { SITE } from '../../config/site'
import BrandLogo from '../../components/BrandLogo'

export const APPS = [
  { id: 'restaurants', icon: UtensilsCrossed, title: 'Restaurants & cafés', desc: 'QR menu, POS, kitchen screen and delivery.' },
  { id: 'stores', icon: ShoppingBag, title: 'Online stores', desc: 'Products, stock, shipping and online payment.' },
  { id: 'bookings', icon: CalendarCheck, title: 'Bookings', desc: 'Salons, clinics and services booked online.' },
]

export function Logo({ className = '' }) {
  return <BrandLogo className={className} imageClassName="h-9 w-9" nameClassName="text-lg font-extrabold tracking-tight text-slate-900" />
}

function Header() {
  const [open, setOpen] = useState(false)
  const [apps, setApps] = useState(false)
  const location = useLocation()
  useEffect(() => { setOpen(false); setApps(false) }, [location.pathname])

  const links = [['/pricing', 'Pricing'], ['/blog', 'Blog'], ['/about', 'About'], ['/contact', 'Contact']]
  const linkClass = ({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium ${isActive ? 'text-slate-900' : 'text-slate-600 hover:text-slate-900'}`

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200/70 bg-white/90 backdrop-blur">
      <div className="mx-auto flex h-16 max-w-6xl items-center justify-between gap-4 px-4 sm:px-6">
        <Link to="/" aria-label={t('{v0} home', { v0: SITE.name })}><Logo /></Link>

        <nav className="hidden items-center gap-1 md:flex" aria-label={t('Main navigation')}>
          <div className="relative" onMouseLeave={() => setApps(false)}>
            <button type="button" onClick={() => setApps(v => !v)} onMouseEnter={() => setApps(true)} aria-expanded={apps}
              className="inline-flex items-center gap-1 rounded-lg px-3 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
              {t('Products')} <ChevronDown className="h-4 w-4" />
            </button>
            {apps && (
              <div className="absolute start-0 top-full w-80 pt-2">
                <div className="rounded-2xl border border-slate-200 bg-white p-2 shadow-xl">
                  {APPS.map(app => (
                    <a key={app.id} href={`/#${app.id}`} className="flex gap-3 rounded-xl p-3 hover:bg-slate-50">
                      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-blue-50 text-blue-700"><app.icon className="h-5 w-5" /></span>
                      <span><span className="block text-sm font-semibold text-slate-900">{t(app.title)}</span><span className="block text-xs text-slate-500">{t(app.desc)}</span></span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
          {links.map(([to, label]) => <NavLink key={to} to={to} className={linkClass}>{t(label)}</NavLink>)}
        </nav>

        <div className="flex items-center gap-2">
          <LanguageSwitcher className="hidden sm:inline-flex" />
          <a href="/admin/login" className="hidden px-2 text-sm font-medium text-slate-700 hover:text-slate-900 sm:inline">{t('Log in')}</a>
          <Link to="/get-started" className="hidden rounded-xl bg-blue-700 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-800 sm:inline-flex">{t('Start free')}</Link>
          <button type="button" className="rounded-lg p-2 text-slate-700 md:hidden" onClick={() => setOpen(v => !v)} aria-expanded={open} aria-label={t(open ? 'Close menu' : 'Open menu')}>
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="border-t border-slate-200 bg-white px-4 pb-5 pt-2 md:hidden">
          <p className="px-1 pt-2 text-xs font-semibold uppercase tracking-wide text-slate-400">{t('Products')}</p>
          {APPS.map(app => <a key={app.id} href={`/#${app.id}`} onClick={() => setOpen(false)} className="flex items-center gap-3 py-2.5 text-slate-800"><app.icon className="h-5 w-5 text-blue-700" />{t(app.title)}</a>)}
          <div className="my-2 border-t border-slate-100" />
          {links.map(([to, label]) => <Link key={to} to={to} className="block py-2.5 text-slate-800">{t(label)}</Link>)}
          <a href="/admin/login" className="block py-2.5 text-slate-800">{t('Log in')}</a>
          <div className="mt-3 flex items-center justify-between gap-3">
            <LanguageSwitcher />
            <Link to="/get-started" className="flex-1 rounded-xl bg-blue-700 py-2.5 text-center font-semibold text-white">{t('Start free')}</Link>
          </div>
        </div>
      )}
    </header>
  )
}

function Footer() {
  const cols = [
    ['Products', APPS.map(a => [`/#${a.id}`, a.title])],
    ['Company', [['/about', 'About'], ['/blog', 'Blog'], ['/pricing', 'Pricing'], ['/contact', 'Contact']]],
    ['Legal', [['/privacy', 'Privacy policy'], ['/terms', 'Terms of service']]],
  ]
  return (
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="mx-auto grid max-w-6xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_repeat(3,1fr)]">
        <div>
          <Logo />
          <p className="mt-3 max-w-xs text-sm leading-6 text-slate-600">{t('Websites, orders, delivery and bookings for businesses in Oman and the Gulf - in Arabic and English.')}</p>
          <ul className="mt-4 space-y-2 text-sm text-slate-600">
            <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-slate-400" />{t(SITE.city)}</li>
            {SITE.email && <li><a href={`mailto:${SITE.email}`} className="flex items-center gap-2 hover:text-slate-900"><Mail className="h-4 w-4 text-slate-400" />{SITE.email}</a></li>}
            {SITE.whatsapp && <li><a href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-slate-900"><MessageCircle className="h-4 w-4 text-slate-400" /><span dir="ltr">+{SITE.whatsapp}</span></a></li>}
          </ul>
        </div>
        {cols.map(([title, items]) => (
          <div key={title}>
            <p className="text-sm font-semibold text-slate-900">{t(title)}</p>
            <ul className="mt-3 space-y-2 text-sm text-slate-600">
              {items.map(([href, label]) => <li key={href}>{href.startsWith('/#') ? <a href={href} className="hover:text-slate-900">{t(label)}</a> : <Link to={href} className="hover:text-slate-900">{t(label)}</Link>}</li>)}
            </ul>
          </div>
        ))}
      </div>
      <div className="border-t border-slate-200">
        <p className="mx-auto max-w-6xl px-4 py-5 text-xs text-slate-500 sm:px-6">© {new Date().getFullYear()} {SITE.company}. {t('All rights reserved.')}</p>
      </div>
    </footer>
  )
}

// The marketing site lives on the main domain only; on a store's own
// subdomain these pages send visitors to the store.
export default function SiteLayout({ children }) {
  const tenant = useTenant()
  const location = useLocation()
  useEffect(() => { if (!location.hash) window.scrollTo(0, 0) }, [location.pathname, location.hash])
  if (tenant.loading) return null
  if (tenant.found) return <Navigate to="/" replace />
  return (
    <div className="min-h-screen bg-white text-slate-900 antialiased">
      <Header />
      <main id="main-content">{children}</main>
      <Footer />
    </div>
  )
}

// Shared top of an inner page (pricing, about, blog...).
export function PageIntro({ eyebrow, title, children }) {
  return (
    <section className="border-b border-slate-100 bg-gradient-to-b from-blue-50/60 to-white">
      <div className="mx-auto max-w-3xl px-4 py-14 text-center sm:px-6 sm:py-20">
        {eyebrow && <p className="text-sm font-semibold text-blue-700">{eyebrow}</p>}
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-5xl">{title}</h1>
        {children && <div className="mt-4 text-lg leading-8 text-slate-600">{children}</div>}
      </div>
    </section>
  )
}
